import { ReactComponent as BookingsIcon } from '@/assets/icons/bookings_icon.svg';
import { Button, Text } from '@/components/DesignSystem';
import { TableRow } from '@/components/DesignSystem/Table/Table';
import { ApiRetailer } from '@api/interfaces';
import { ApiOrderTransaction } from '@api/interfaces/ApiOrderTransaction';
import { ApiPointsHistory } from '@api/interfaces/ApiPointsHistory';
import { Stack, Tooltip, useTheme } from '@mui/material';
import { formatCurrency, formatNumber, formatMediumDate, getAccountName } from '@shared/utilities';
import { getMostRecentDescription, getPointsTotalsByType } from '@shared/utilities/PointsUtils';
import i18next from 'i18next';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getTransactionTypeLabel, isOrderAssociatedWithBooking } from './helpers';

type PointsActivityTableRowProps = {
  showBottomBorder?: boolean;
  onDetailsClick?: () => void;
  retailer?: ApiRetailer;
} & (
  | { transaction: ApiOrderTransaction; pointsHistory?: never }
  | { transaction?: never; pointsHistory: ApiPointsHistory }
);

export const PointsActivityTableRow: React.FC<PointsActivityTableRowProps> = ({
  showBottomBorder = false,
  onDetailsClick,
  retailer,
  transaction,
  pointsHistory,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const brandColor = retailer?.brandColor ?? theme.palette.primary.main;

  const isManuallyIssued = !transaction;

  const formatQuantity = (): string | null => {
    if (isManuallyIssued) {
      return null;
    }
    if (
      transaction.orderSubtype !== 'scale-ticket' &&
      transaction.orderSubtype !== 'settlement'
    ) {
      return null;
    }
    if (!transaction.products || transaction.products.length === 0) {
      return null;
    }
    let totalQuantity = 0;
    let uom = 'bu';
    transaction.products.forEach((product) => {
      if (product.quantity) {
        totalQuantity += product.quantity;
      }
      if (product.uom) {
        uom = product.uom;
      }
    });
    const formattedQuantity = new Intl.NumberFormat(i18next.language, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(totalQuantity);
    return `${formattedQuantity} ${uom}`;
  };

  const quantity = formatQuantity();

  const { pointsEarned, pointsRedeemed } = useMemo(() => {
    const histories = isManuallyIssued
      ? [{ amount: pointsHistory.amount, type: pointsHistory.type }]
      : transaction.pointHistories ?? [];
    const totals = getPointsTotalsByType(histories);
    return {
      pointsEarned: totals.earned - totals.returned,
      pointsRedeemed: totals.redeemed - totals.refunded,
    };
  }, [isManuallyIssued, transaction, pointsHistory]);

  const accountName = useMemo(() => {
    if (isManuallyIssued) {
      return pointsHistory.user ? getAccountName(pointsHistory.user) : '-';
    }
    if (!transaction.farmer) {
      return '-';
    }
    return getAccountName(transaction.farmer);
  }, [isManuallyIssued, transaction, pointsHistory]);

  const externalId = useMemo(() => {
    if (isManuallyIssued) {
      return pointsHistory.user?.externalId ?? null;
    }
    return transaction.farmer?.externalId ?? null;
  }, [isManuallyIssued, transaction, pointsHistory]);

  const mostRecentDescription = useMemo(() => {
    if (isManuallyIssued) {
      return pointsHistory.description ?? null;
    }
    return getMostRecentDescription(transaction.pointHistories ?? []);
  }, [isManuallyIssued, transaction, pointsHistory]);

  const orderDate = isManuallyIssued ? pointsHistory.orderDate : transaction.orderDate;

  const typeLabel = isManuallyIssued
    ? t($ => $.customerDashboard.pointsActivity.adjustment)
    : getTransactionTypeLabel(transaction, t);

  return (
    <TableRow
      sx={{
        '& > td': {
          borderBottom: showBottomBorder ? 'none' : undefined,
        },
      }}
      values={[
        <Text category="body-medium" key="date">
          {formatMediumDate(orderDate, i18next)}
        </Text>,
        <Stack
          alignItems="center"
          direction="row"
          key="transaction-type"
          sx={{ minWidth: 0 }}
        >
          <Text
            category="body-medium"
            sx={{ lineBreak: 'anywhere' }}
          >
            {typeLabel}
            {!isManuallyIssued && (
              <span style={{ color: theme.palette.text.secondary }}>
                {' '}{transaction.externalId}{' '}
              </span>
            )}
            {!isManuallyIssued && isOrderAssociatedWithBooking(transaction) && (
              <BookingsIcon
                style={{
                  color: brandColor,
                  height: '16px',
                  verticalAlign: 'middle',
                  width: '13px',
                }}
              />
            )}
          </Text>
        </Stack>,
        <Tooltip
          key="description"
          title={mostRecentDescription && mostRecentDescription.length > 50
            ? mostRecentDescription
            : ''
          }
        >
          <span>
            <Text category="body-medium">
              {mostRecentDescription
                ? (mostRecentDescription.length > 50
                  ? `${mostRecentDescription.slice(0, 50)}…`
                  : mostRecentDescription)
                : '-'}
            </Text>
          </span>
        </Tooltip>,
        <Stack key="customer">
          <Text category="body-medium">
            {accountName}
          </Text>
          {externalId && (
            <Text category="body-small" sx={{ color: theme.palette.text.secondary }}>
              {t($ => $.common.labels.erpId, { id: externalId })}
            </Text>
          )}
        </Stack>,
        <Text category="body-medium" key="points-earned">
          {pointsEarned ? formatNumber(pointsEarned, i18next) : '-'}
        </Text>,
        <Text category="body-medium" key="points-redeemed">
          {pointsRedeemed ? formatNumber(pointsRedeemed, i18next) : '-'}
        </Text>,
        <Stack key="total">
          {!isManuallyIssued && (
            <Text category="body-medium">
              {formatCurrency(transaction.totalCost, i18next)}
            </Text>
          )}
          {!isManuallyIssued && quantity && (
            <Text category="body-small" sx={{ color: theme.palette.text.secondary }}>
              {quantity}
            </Text>
          )}
          {isManuallyIssued && (
            <Text category="body-medium">-</Text>
          )}
        </Stack>,
        <Stack alignItems="end" key="details">
          <Button
            onClick={() => onDetailsClick?.()}
            square
            testID={`points-activity-details-${isManuallyIssued ? pointsHistory.id : transaction.id}`}
            variant="contained"
          >
            {t($ => $.common.actions.details)}
          </Button>
        </Stack>,
      ]}
    />
  );
};
